import { useTopicRefresh } from '@src/shared/realtime';
import { type TopicMessage, useWebSocketTopic } from '@src/shared/websocket';
import { useCallback } from 'react';
import { parseEditingSnapshot } from '../lib/editing';
import { invalidateAbsenceQueries } from '../model/query';
import {
  ABSENCE_CHANGED_TOPIC,
  ABSENCE_EDITING_TOPIC,
  setAbsenceConnectionId,
  setAbsenceEditingSnapshot,
} from '../model/realtime';

/**
 * Keeps the absence timeline from going stale under the viewer.
 *
 * An invalidation topic: the message says only that something moved, so the
 * client goes and asks. That costs one refetch per connected browser per
 * event, which is affordable because absence writes are rare -- and it is
 * necessary because what a viewer may see of a pending change depends on who
 * they are, so there is no single payload that would be correct for everyone.
 *
 * `useTopicRefresh` also refreshes on connect and when the tab becomes
 * visible again, which covers the messages a backgrounded tab never saw.
 */
export default function AbsenceSocketController() {
  const refresh = useCallback(() => {
    void invalidateAbsenceQueries();
  }, []);

  const ignoreMessage = useCallback(() => {
    // Nothing to apply in place; the refresh below is the whole response.
  }, []);

  // The opposite shape to the topic above: this one carries the whole set of
  // held locks and is applied in place, the way `users.presence` is. It drives
  // a banner and the waiting client's re-acquire, not a query.
  const handleEditing = useCallback((message: TopicMessage) => {
    const parsed = parseEditingSnapshot(message);
    if (!parsed) return;
    // Before the snapshot: a new id resets the ordering the snapshot is
    // checked against, and tells holders to revalidate.
    if (parsed.connectionId !== undefined) {
      setAbsenceConnectionId(parsed.connectionId);
    }
    setAbsenceEditingSnapshot(parsed.locks, parsed.readAt);
  }, []);

  useTopicRefresh(ABSENCE_CHANGED_TOPIC, ignoreMessage, refresh);
  useWebSocketTopic(ABSENCE_EDITING_TOPIC, handleEditing);

  return null;
}
